import { useSelectState as useAriaKitSelectState } from "ariakit";
import React from "react";
import { unstable_batchedUpdates as batch } from "react-dom";
import { SelectPopover } from "../SelectPopover_v2";
import { useEvent } from "../hooks/useEvent";
import { forkedRef } from "../utils/forkedRef";
import { LooseTextInput } from "./LooseTextInput";
import { PropOptionRenderItem } from "../PropX";

type Props = {
  id?: string;
  name?: string;
  value: string;
  items: PropOptionRenderItem[];
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  mono?: boolean;
  leftSide?: React.ReactNode;
  rightSide?: React.ReactNode;
  pattern?: string;
  invalid?: boolean;
  spellCheck?: boolean;
  readOnly?: boolean;
  tabIndex?: 0 | -1;
  onSelect(value: string): void;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  onInvalid?: React.FormEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLDivElement>;
  onFocus?: React.FocusEventHandler<HTMLDivElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onPaste?: React.ClipboardEventHandler<HTMLInputElement>;
  listHeader?: React.ReactNode;
  listFooter?: React.ReactNode;
  UNSAFE_borderRadiusTop?: "base" | "none";
  UNSAFE_popoverRepositionKey?: string | number;
  UNSAFE_showPopoverOnInvalid?: boolean;
  UNSAFE_forceHidePopover?: boolean;
  UNSAFE_precalculateItemHeights?: boolean;
  UNSAFE_overscanCount?: number;
  UNSAFE_type?: "number" | "text" | "password" | "email" | "tel" | "url" | "search" | "textarea";
  UNSAFE_rows?: number;

  "data-testid"?: string;
};

const identity = (x: string) => x;

export const LooseAutocompleteInput = React.forwardRef<HTMLInputElement, Props>((props, forwardedRef) => {
  const {
    name,
    id = name,
    value,
    items,
    leftSide,
    rightSide,
    pattern,
    onInvalid,
    onSelect,
    onKeyDown,
    onPaste,
    listHeader = null,
    listFooter = null,
    placeholder = "",
    spellCheck = false,
    disabled = false,
    required = false,
    readOnly = false,
    mono = false,
    invalid = false,
    tabIndex = 0,
    UNSAFE_rows,
    UNSAFE_borderRadiusTop = "base",
    UNSAFE_popoverRepositionKey,
    UNSAFE_showPopoverOnInvalid = false,
    UNSAFE_precalculateItemHeights = false,
    UNSAFE_forceHidePopover = false,
    UNSAFE_overscanCount = 10,
    UNSAFE_type = "search",
  } = props;

  const inputRef = React.useRef<HTMLInputElement>(null);

  /**
   * This ref is used to get the correct blurring behavior to work. That is,
   *
   * If the user typed something in between selecting any dropdown value then it should create a tag from it on blur.
   * If the user typed in order to find a specific tag from the dropdown, then we should NOT create a tag from it on blur.
   *
   * Essentially, we assume the if the user typed and selected a value, what is currently in the input was a search term.
   */
  const searchChangedSinceLastSelectedRef = React.useRef(false);

  const onChange = useEvent<React.ChangeEventHandler<HTMLInputElement>>((ev) => {
    props.onChange(ev);

    /**
     * Set this to true because the search has now changed.
     */
    searchChangedSinceLastSelectedRef.current = true;
  });

  const state = useAriaKitSelectState<string[]>({
    placement: "bottom",
    gutter: -1,
    value: [],
    sameWidth: true,
    virtualFocus: false,
    includesBaseElement: false,
    flip: false,
    /**
     * This function is called when a token is selected from the dropdown.
     * We expect that `keys` in this case is always an array of length 1.
     */
    setValue([key]) {
      batch(() => {
        /**
         * Set this to false because a token was selected by clicking on it.
         * If there was only one option in one group and that value was selected,
         * then we want to focus the input.
         */
        searchChangedSinceLastSelectedRef.current = false;

        onSelect(key);

        /**
         * Here we want to decide where to put the focus after selecting a token.
         * There are three cases:
         *  1. If there are no more options in the dropdown, then focus the input. (Can't actually happen, but we need to make TS happy.)
         *  2. If there is only one option in the dropdown, then focus the input.
         *  3. If there are more than one options in the dropdown, then focus the next option in the list.
         *
         * The following values are used to check how many options remain in the dropdown.
         * We can't simply check the length of `items` because it includes other renderable items such as titles and spacers.
         * We also only care about the length of the options in so much as there is zero, one, or _more_ than one.
         */
        type OptionsCheck = { type: "none" } | { type: "one"; option: PropOptionRenderItem & { type: "option" } } | { type: "many" };
        let check: OptionsCheck = { type: "none" };

        for (let i = 0; i < items.length; i++) {
          const item = items[i];

          if (item.type !== "option") {
            /**
             * Skip any elements that are not options.
             */
            continue;
          }

          if (check.type === "none") {
            check = { type: "one", option: item };
            continue;
          } else if (check.type === "one") {
            check = { type: "many" };
            /**
             * If we have at least two options, then we don't need to iterate anymore.
             */
            break;
          }
        }

        switch (check.type) {
          /**
           * If there were no options, then do nothing.
           * Really this can't happen because the menu would be hidden,
           * but we check it for completeness.
           */
          case "none":
            return;
          /**
           * If there was only one option, then it was the one that was selected, so focus the input.
           */
          case "one":
            inputRef.current?.focus();
            return;
          /**
           * If there was more than one option, then focus the next option in the list.
           */
          case "many":
            if (state.activeId === state.last()) {
              /**
               * When selecting a token from the dropdown, we want to make sure that we
               * highlight the next token in the list. If we don't do this then the menu
               * will lose focus and the user will have to click on it again to get it back.
               */
              state.move(state.previous());
            } else {
              state.move(state.next());
            }
        }
      });
    },
  });

  /**
   * Ensure that an item is always able to be tabbed to from the input element.
   * If we ever find that the "active" item is not longer part of the list, we should reset it
   * to the first item in the list.
   */
  React.useEffect(() => {
    const currentItem = state.items.find((i) => i.ref.current !== undefined && i.id === state.activeId);

    if (!currentItem) {
      state.setActiveId(state.first());
    }
  }, [value, state.items.reduce((a, b) => a + b.id, ""), items.length]);

  /**
   * When the menu is disabled, hide it so that it doesn't stay open and unclickable.
   */
  React.useEffect(() => {
    if (disabled) {
      state.hide();
    }
  }, [disabled]);

  const onFocus = useEvent<React.FocusEventHandler<HTMLInputElement>>((ev) => {
    if (state.open) {
      return;
    }

    state.show();

    /**
     * Sets the focus back on the input when focusing the input for the first time. Because if `state.activeId` has been
     * set, then the input will be blurred when the select menu opens. We obviously don't want this in the case where
     * the input is focused, so just force the input to reclaim the focus.
     */
    queueMicrotask(() => inputRef.current?.focus());

    props.onFocus?.(ev);
  });

  const onBlur = useEvent<React.FocusEventHandler<HTMLInputElement>>((ev) => {
    if (ev.currentTarget.contains(ev.relatedTarget)) {
      return;
    }

    props.onBlur?.(ev);
  });

  let popover: React.ReactNode = null;

  const showPopover = !invalid || UNSAFE_showPopoverOnInvalid;

  if (showPopover) {
    popover = (
      <SelectPopover
        state={state}
        id={`${name}-select-menu`}
        width="auto"
        closeOnEscape={false}
        autoFocusOnShow={false}
        autoFocusOnHide={false}
        items={items}
        mono={mono}
        listHeader={listHeader}
        listFooter={listFooter}
        UNSAFE_repositionKey={UNSAFE_popoverRepositionKey}
        UNSAFE_forceHide={UNSAFE_forceHidePopover}
        UNSAFE_overscanCount={UNSAFE_overscanCount}
        UNSAFE_precalculateItemHeights={UNSAFE_precalculateItemHeights}
        data-testid={identity}
      />
    );
  }

  return (
    <div onBlurCapture={onBlur} onFocusCapture={onFocus} ref={state.anchorRef as React.Ref<HTMLDivElement>}>
      <LooseTextInput
        type={UNSAFE_type}
        autoComplete="off"
        data-testid={props["data-testid"]}
        id={id}
        name={name}
        ref={forkedRef(inputRef, forwardedRef)}
        placeholder={placeholder}
        value={value}
        UNSAFE_borderRadiusBottomLeft={items.length > 0 && state.open && showPopover && !UNSAFE_forceHidePopover ? "none" : "base"}
        UNSAFE_borderRadiusBottomRight={items.length > 0 && state.open && showPopover && !UNSAFE_forceHidePopover ? "none" : "base"}
        UNSAFE_borderRadiusTopLeft={UNSAFE_borderRadiusTop}
        UNSAFE_borderRadiusTopRight={UNSAFE_borderRadiusTop}
        rows={UNSAFE_rows}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        disabled={disabled}
        required={required}
        mono={mono}
        readOnly={readOnly}
        leftSide={leftSide}
        rightSide={rightSide}
        pattern={pattern}
        invalid={invalid}
        tabIndex={tabIndex}
        spellCheck={spellCheck}
        onInvalid={onInvalid}
        onChange={onChange}
      />
      {popover}
    </div>
  );
});
